import { useMemo, useCallback, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Text,
  Button,
  Flex,
  Separator,
  Container,
  ScrollArea,
  IconButton,
  Avatar,
  Em,
} from "@radix-ui/themes";
import { ArrowLeftIcon, Pencil2Icon } from "@radix-ui/react-icons";
import { useReleases } from "@/routes/simulator/context/release";
import { SimulatorChannelIcon } from "@/routes/simulators/SimulatorChannelIcon";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/v2/components/dataTabs/tabs";
import Select from "@/components/shared/select";
import { InlineTextField } from "@/components/shared/inputs/inilie-text-field/InlineTextField";
import { toast } from "sonner";
import {
  RegressionSetFrequency,
  RegressionSetFrequencyDayOfWeek,
  RegressionSetFrequencyType,
  UpdateRegressionSetPayload,
} from "@/v2/entities/regression/Regression.model";
import { useRegressionApi } from "@/v2/entities/regression/Regression.api";
import { useMutation, useQuery } from "react-query";
interface SetConfiguration {
  name: string;
  frequency: RegressionSetFrequency;
  numberOfConversations: number;
  releaseToTestAgainstId?: string;
}

const dayOptions = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" },
];

// Step component matching the scorecard structure
function Step({
  index,
  label,
  description,
  children,
}: {
  index: number;
  label: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <Flex gap={"2"}>
      <Flex width={"400px"} flexShrink={"0"} gap={"4"}>
        <Avatar size="2" radius="full" fallback={index.toString()} />

        <Flex pt={"3px"} direction={"column"} gap={"1"} align={"start"}>
          <Text size={"4"} color={"gray"} weight={"medium"} highContrast>
            {label}
          </Text>

          {description && (
            <Text size={"3"} color={"gray"}>
              {description}
            </Text>
          )}
        </Flex>
      </Flex>

      <Flex flexGrow={"1"}>{children}</Flex>
    </Flex>
  );
}

// FieldGroup component matching the scorecard structure
function FieldGroup({
  label,
  description,
  error,
  children,
}: {
  label?: string;
  description?: string;
  noSeparator?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <Flex gap={"2"} direction={"column"}>
      <Box>
        {(label != null || description != null) && (
          <Flex direction={"column"} gap={"1"}>
            {label != null && (
              <Text size={"2"} as={"label"}>
                {label}
              </Text>
            )}

            {description != null && (
              <Text size={"2"} color={"gray"}>
                <Em>{description}</Em>
              </Text>
            )}
          </Flex>
        )}

        <Flex>{children}</Flex>
      </Box>

      {error != null && (
        <Text size={"1"} color={"red"}>
          <Em>{error}</Em>
        </Text>
      )}
    </Flex>
  );
}

export function SetConfigurationPage() {
  const { agentId, setId } = useParams();
  const navigate = useNavigate();
  const { simulators } = useReleases();
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState<SetConfiguration>({
    name: "",
    frequency: {
      type: RegressionSetFrequencyType.WEEKLY,
      dayOfWeek: RegressionSetFrequencyDayOfWeek.MONDAY,
    },
    numberOfConversations: 50,
    releaseToTestAgainstId: undefined,
  });

  const agent = useMemo(() => {
    return simulators?.find((s: any) => s.id.toString() === agentId);
  }, [simulators, agentId]);

  const { getRegressionSet, updateRegressionSet } = useRegressionApi();

  const { data: setInfo, refetch: refetchSetInfo } = useQuery({
    queryKey: ["regression-set", setId],
    queryFn: () => getRegressionSet(setId || ""),
  });

  const updateSetMutation = useMutation({
    mutationKey: ["update-regression-set"],
    mutationFn: ({
      regressionSetId,
      input,
    }: {
      regressionSetId: string;
      input: UpdateRegressionSetPayload;
    }) => updateRegressionSet(regressionSetId, input),
  });

  // Initialize config with set name when setInfo is loaded
  useMemo(() => {
    if (setInfo?.name && config.name === "") {
      setConfig((prev) => ({
        ...prev,
        name: setInfo.name,
        frequency: setInfo.frequency || {
          type: RegressionSetFrequencyType.WEEKLY,
          dayOfWeek: RegressionSetFrequencyDayOfWeek.MONDAY,
        },
      }));
    }
  }, [setInfo?.name, config.name]);

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    try {
      await updateSetMutation.mutateAsync({
        regressionSetId: setId || "",
        input: {
          name: config.name,
          description: setInfo?.description,
          frequency: config.frequency,
        },
      });
      toast.success("Configuration saved successfully");
      refetchSetInfo();
      navigate(`/training-center/${agentId}/regression-sets/${setId}`);
    } catch (error) {
      console.error("Failed to save configuration:", error);
      toast.error("Failed to save configuration");
    } finally {
      setLoading(false);
    }
  }, [
    navigate,
    agentId,
    setId,
    config,
    updateSetMutation,
    refetchSetInfo,
    setInfo?.description,
  ]);

  const updateConfig = useCallback(
    (key: keyof SetConfiguration, value: any) => {
      setConfig((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  if (!agent) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 2000,
          background: "white",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Flex justify="center" align="center" style={{ minHeight: "400px" }}>
          <Text size="3" color="gray">
            Agent not found
          </Text>
        </Flex>
      </div>
    );
  }

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 2000,
        background: "white",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Flex
        justify="start"
        align="center"
        py={"4"}
        px={"8"}
        gap={"4"}
        style={{
          borderBottom: "1px solid var(--q-border)",
          boxShadow: "var(--shadow-1)",
        }}
      >
        <IconButton
          variant="ghost"
          color="gray"
          onClick={() =>
            navigate(`/training-center/${agentId}/regression-sets/${setId}`)
          }
        >
          <ArrowLeftIcon />
        </IconButton>

        <Text size="2" color="gray" weight="medium" highContrast>
          Configure Set: {setInfo?.name}
        </Text>
      </Flex>

      <ScrollArea style={{ flexGrow: 1 }}>
        <Container size="4" style={{ paddingBlock: 32 }}>
          <Flex direction={"column"}>
            <Button
              variant="solid"
              color="gray"
              size="2"
              highContrast
              disabled={loading}
              onClick={handleSubmit}
              className="fixed top-[10px] right-[48px]"
            >
              {loading ? "Saving..." : "Save Configuration"}
            </Button>

            <Flex direction={"column"} gap={"8"}>
              <FieldGroup label="Set Name" noSeparator>
                <Flex
                  align="center"
                  justify="between"
                  style={{ width: "100%" }}
                >
                  <Flex align="center" gap="2">
                    <InlineTextField
                      size="3"
                      value={config.name || setInfo?.name}
                      onChange={(value) => updateConfig("name", value)}
                      placeholder="Enter set name"
                      icon={
                        <Pencil2Icon width={16} height={16} className="ml-2" />
                      }
                    />
                  </Flex>
                  <Flex align="center" gap="2">
                    <SimulatorChannelIcon channel={agent.channel} size="2" />
                    <Text size="3">{agent.name}</Text>
                  </Flex>
                </Flex>
              </FieldGroup>

              <Separator orientation="horizontal" size="4" />

              <Step
                index={1}
                label="Schedule"
                description="Set when the test will run"
              >
                <Flex direction={"column"} gap={"2"}>
                  <Text size="2" color="gray" weight="medium">
                    Schedule Type
                  </Text>
                  <Tabs
                    value={config.frequency.type}
                    onValueChange={(value) =>
                      updateConfig("frequency", {
                        type: value as RegressionSetFrequencyType,
                        dayOfWeek: RegressionSetFrequencyDayOfWeek.MONDAY,
                        dayOfMonth: 1,
                      })
                    }
                  >
                    <TabsList>
                      <TabsTrigger value="weekly">Weekly</TabsTrigger>
                      <TabsTrigger value="monthly">Monthly</TabsTrigger>
                    </TabsList>

                    <TabsContent value="weekly">
                      <Flex
                        direction="column"
                        gap="4"
                        style={{ marginTop: "16px" }}
                      >
                        <Flex direction="column" gap="2">
                          <Text size="2" color="gray" weight="medium">
                            Day of Week
                          </Text>
                          <Select
                            options={dayOptions}
                            value={config.frequency.dayOfWeek}
                            onChange={(value) =>
                              updateConfig("frequency", {
                                type: RegressionSetFrequencyType.WEEKLY,
                                dayOfWeek:
                                  value as RegressionSetFrequencyDayOfWeek,
                                dayOfMonth: undefined,
                              })
                            }
                          />
                        </Flex>

                        <div
                          style={{
                            padding: "8px 12px",
                            background: "#EFF6FF",
                            border: "1px solid #BFDBFE",
                            borderRadius: 6,
                            color: "#1E40AF",
                            fontSize: 14,
                            lineHeight: 1.5,
                            display: "inline-block",
                            maxWidth: "fit-content",
                          }}
                        >
                          🕐 Test will run every{" "}
                          {
                            dayOptions.find(
                              (d) => d.value === config.frequency.dayOfWeek
                            )?.label
                          }{" "}
                          at 9:00 AM (UTC time)
                        </div>
                      </Flex>
                    </TabsContent>

                    <TabsContent value="monthly">
                      <Flex
                        direction="column"
                        gap="4"
                        style={{ marginTop: "16px" }}
                      >
                        <div
                          style={{
                            padding: "8px 12px",
                            background: "#EFF6FF",
                            border: "1px solid #BFDBFE",
                            borderRadius: 6,
                            color: "#1E40AF",
                            fontSize: 14,
                            lineHeight: 1.5,
                            display: "inline-block",
                            maxWidth: "fit-content",
                          }}
                        >
                          🕐 Test will run every 1st of the month at 9:00 AM
                          (UTC time)
                        </div>
                      </Flex>
                    </TabsContent>
                  </Tabs>
                </Flex>
              </Step>

              <Separator orientation="horizontal" size="4" />
            </Flex>
          </Flex>
        </Container>
      </ScrollArea>
    </div>
  );
}
